'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Heart, Mail, ShieldCheck, Sparkles } from 'lucide-react';

interface FlexibleSupportCardProps {
  organizationName: string;
  onSubscribe: (amount: number, email: string) => void;
  onOpenModal?: (amount: number) => void;
  isLoading?: boolean;
  currency?: string;
  minAmount?: number;
  suggestedAmounts?: number[];
  defaultAmount?: number;
  /** Si la org ya tiene planes fijos publicados */
  hasPublishedPlans?: boolean;
  compact?: boolean;
}

const DEFAULT_BENEFITS = [
  'Apoyo directo a la organización',
  'Contenido y novedades exclusivas',
  'Comunidad de miembros',
  'Cancelación en cualquier momento',
];

const currencyFormatters = new Map<string, Intl.NumberFormat>();
function getCurrencyFormatter(currency: string) {
  if (!currencyFormatters.has(currency)) {
    currencyFormatters.set(
      currency,
      new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }),
    );
  }
  return currencyFormatters.get(currency)!;
}

export function FlexibleSupportCard({
  organizationName,
  onSubscribe,
  onOpenModal,
  isLoading,
  currency = 'CLP',
  minAmount = 1000,
  suggestedAmounts = [1500, 3000, 5000, 10000],
  defaultAmount = 3000,
  hasPublishedPlans = false,
  compact = false,
}: FlexibleSupportCardProps) {
  const [amount, setAmount] = useState(defaultAmount);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const formatter = getCurrencyFormatter(currency);
  const useModalFlow = Boolean(onOpenModal);
  const quickAmounts = suggestedAmounts.length > 0 ? suggestedAmounts : [defaultAmount];

  useEffect(() => {
    setAmount(defaultAmount);
  }, [defaultAmount]);

  const handleSubmit = async () => {
    if (amount < minAmount) return;
    if (useModalFlow) {
      onOpenModal!(amount);
      return;
    }
    if (!email.includes('@')) return;

    setSubscribing(true);
    try {
      await onSubscribe(amount, email);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-3xl border animate-in fade-in slide-in-from-bottom-6 duration-700"
      style={{
        borderColor: 'color-mix(in srgb, var(--subs-accent) 25%, transparent)',
        background:
          'linear-gradient(160deg, color-mix(in srgb, var(--subs-accent) 12%, transparent) 0%, hsl(var(--card)/0.4) 50%, transparent 100%)',
      }}
    >
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl opacity-40"
        style={{ background: 'var(--subs-accent)' }}
      />

      <div
        className={`relative grid gap-8 p-6 sm:p-10 ${compact ? '' : 'lg:grid-cols-[1fr_1.1fr]'}`}
      >
        <div className="space-y-5">
          <div
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium"
            style={{
              borderColor: 'color-mix(in srgb, var(--subs-accent) 35%, transparent)',
              color: 'var(--subs-accent)',
            }}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Aporte libre
          </div>

          <div>
            <h2 className="font-[family-name:var(--font-outfit)] text-3xl font-bold tracking-tight sm:text-4xl">
              Elige cuánto apoyar
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              {hasPublishedPlans
                ? `¿Quieres aportar más o menos que los planes publicados? Define el monto mensual que prefieras para apoyar a ${organizationName}.`
                : `${organizationName} aún no tiene planes fijos publicados. Puedes suscribirte con el monto mensual que prefieras.`}
            </p>
          </div>

          <ul className="space-y-2.5">
            {DEFAULT_BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-center gap-2.5 text-sm text-foreground/85">
                <Check className="h-4 w-4 shrink-0 text-[var(--subs-accent)]" />
                {benefit}
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
            <ShieldCheck className="h-4 w-4 text-[var(--subs-accent)]" />
            Mercado Pago · Sin permanencia
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 bg-background/40 p-5 sm:p-6 backdrop-blur-sm space-y-5">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Montos sugeridos</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {quickAmounts.map((quickAmount) => (
                <button
                  key={quickAmount}
                  type="button"
                  onClick={() => setAmount(quickAmount)}
                  disabled={isLoading || subscribing}
                  className={`
                    rounded-xl border px-3 py-3 text-sm font-semibold transition-all duration-200
                    disabled:opacity-50
                    ${amount === quickAmount
                      ? 'scale-[1.02] shadow-lg'
                      : 'border-white/10 hover:border-[color-mix(in_srgb,var(--subs-accent)_30%,transparent)]'
                    }
                  `}
                  style={
                    amount === quickAmount
                      ? {
                          borderColor: 'var(--subs-accent)',
                          background: 'color-mix(in srgb, var(--subs-accent) 15%, transparent)',
                          color: 'var(--subs-accent)',
                        }
                      : undefined
                  }
                >
                  {formatter.format(quickAmount)}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="flex-amount">O ingresa tu monto</Label>
            <div className="relative">
              <Input
                id="flex-amount"
                type="number"
                min={minAmount}
                step={500}
                value={amount || ''}
                onChange={(e) => setAmount(Number(e.target.value) || 0)}
                className="h-14 pr-14 text-center text-xl font-bold"
                disabled={isLoading || subscribing}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {currency}
              </span>
            </div>
            {amount > 0 && amount < minAmount && (
              <p className="text-xs text-destructive">Mínimo {formatter.format(minAmount)}</p>
            )}
          </div>

          {!useModalFlow && (
            <div className="space-y-2">
              <Label htmlFor="flex-email">Email</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="flex-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="pl-9"
                  disabled={isLoading || subscribing}
                />
              </div>
            </div>
          )}

          <div className="rounded-xl border border-white/5 bg-card/30 px-4 py-3 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Tu aporte</p>
            <p
              className="font-[family-name:var(--font-outfit)] text-3xl font-bold mt-1"
              style={{ color: 'var(--subs-accent)' }}
            >
              {formatter.format(amount)}
              <span className="text-base font-normal text-muted-foreground"> / mes</span>
            </p>
          </div>

          <Button
            type="button"
            disabled={
              isLoading ||
              subscribing ||
              amount < minAmount ||
              (!useModalFlow && !email.includes('@'))
            }
            onClick={() => void handleSubmit()}
            className="h-12 w-full rounded-xl text-base font-semibold text-white hover:opacity-90"
            style={{ background: 'var(--subs-accent)' }}
          >
            {subscribing || isLoading ? (
              'Procesando...'
            ) : (
              <>
                <Heart className="mr-2 h-4 w-4 fill-current" />
                Apoyar con {formatter.format(amount)}/mes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
